import * as THREE from "three";
import type { World as SceneWorld } from "./scene";
import type { Cup } from "./types";
import type { DragJoint, PhysicsBody, PhysicsHandle } from "./physics";

const DRAG_THRESHOLD_PX = 5;
const THROW_FEEL_MULT = 1.5;
const VELOCITY_WINDOW_MS = 80;
const MAX_SAMPLES = 16;

export interface InteractionHandles {
  onSelect: (cb: (cup: Cup | null) => void) => void;
  setCups: (cups: Map<string, { group: THREE.Group; cup: Cup }>) => void;
  /**
   * Disable cup picking + drag-to-throw. Pointer events fall through to
   * OrbitControls so the user can still rotate the camera. Used while a
   * cup is in focus mode so the user can inspect by orbiting without the
   * cup following their cursor.
   */
  setEnabled: (enabled: boolean) => void;
}

interface VelSample {
  pos: THREE.Vector3;
  t: number;
}

// Drag sub-state — set by startDrag once a press crosses
// DRAG_THRESHOLD_PX. Owned exclusively by the active press; clearing
// `press` (or its `drag` field) tears the joint and helper down via
// endDrag.
interface DragState {
  body: PhysicsBody;
  helperBody: PhysicsBody;
  joint: DragJoint;
  samples: VelSample[];
  /** Body-local offset of the joint anchor on the cup. Captured at
   * startDrag and reused each pointermove to clamp the helper Y so the
   * joint can't pull the cup body's center below the floor. */
  grabOffsetLocal: THREE.Vector3;
}

// Press state — set on pointerdown when the pointer hit a cup. Stays
// alive through pointermove (where it might escalate into a drag) and
// is cleared on pointerup / cancel / setEnabled(false).
interface PressState {
  group: THREE.Group;
  hit: THREE.Vector3;
  downX: number;
  downY: number;
  /** Non-null once the press has crossed DRAG_THRESHOLD_PX. */
  drag: DragState | null;
}

/**
 * Pointer-based picking, hover, click-to-select, and drag-to-throw.
 *
 * - Hover (no press): subtle uniform scale on the cup. Pure visual.
 * - Click (no drag past threshold): notify subscribers (HUD opens info).
 * - Press + drag past threshold: keep the cup body dynamic and attach a
 *   spherical joint between it and an invisible kinematic helper body
 *   that we move under the cursor. The cup is pulled toward the helper,
 *   but Rapier's solver still resolves collisions — so cups can't be
 *   dragged through shelves or other cups. On release, remove the joint
 *   and impart a velocity computed from the last ~80 ms of cursor motion
 *   plus a small random tumble for feel.
 * - OrbitControls is disabled while a cup press is active so the camera
 *   doesn't whip around mid-grab; it's re-enabled on release.
 */
export function attachInteraction(
  world: SceneWorld,
  physics: PhysicsHandle,
): InteractionHandles {
  const raycaster = new THREE.Raycaster();
  const pointer = new THREE.Vector2();
  const subscribers: Array<(cup: Cup | null) => void> = [];

  let cupsById = new Map<string, { group: THREE.Group; cup: Cup }>();
  let hovered: THREE.Group | null = null;
  let enabled = true;

  // Single binding owns the entire press / drag lifecycle. Reset is
  // `press = null` rather than ten line-by-line nullifications.
  let press: PressState | null = null;

  // Camera-facing plane used to project pointer motion into 3D world
  // space during a drag. Reused — set in startDrag, read in pointermove.
  const dragPlane = new THREE.Plane();
  // Scratch vectors / quat for the per-frame floor-clamp math.
  const tmpRotQuat = new THREE.Quaternion();
  const tmpOffsetWorld = new THREE.Vector3();
  // Cup body's collider half-height in local Y. Match physics.ts CUP_HEIGHT/2.
  const CUP_HALF_HEIGHT = 0.55;

  const canvas = world.renderer.domElement;

  function setHover(g: THREE.Group | null): void {
    if (press?.drag) return; // freeze hover while a cup is held
    if (hovered === g) return;
    if (hovered) hovered.scale.set(1, 1, 1);
    hovered = g;
    if (hovered) hovered.scale.setScalar(1.04);
    canvas.style.cursor = hovered ? "grab" : "default";
  }

  function pickAt(
    clientX: number,
    clientY: number,
  ): { group: THREE.Group; point: THREE.Vector3 } | null {
    const rect = canvas.getBoundingClientRect();
    pointer.x = ((clientX - rect.left) / rect.width) * 2 - 1;
    pointer.y = -((clientY - rect.top) / rect.height) * 2 + 1;
    raycaster.setFromCamera(pointer, world.camera);
    const hits = raycaster.intersectObjects(world.cupRoot.children, true);
    for (const hit of hits) {
      const id = findCupId(hit.object);
      if (id) {
        const entry = cupsById.get(id);
        if (entry) return { group: entry.group, point: hit.point.clone() };
      }
    }
    return null;
  }

  function pointerToPlane(
    clientX: number,
    clientY: number,
    out: THREE.Vector3,
  ): boolean {
    const rect = canvas.getBoundingClientRect();
    pointer.x = ((clientX - rect.left) / rect.width) * 2 - 1;
    pointer.y = -((clientY - rect.top) / rect.height) * 2 + 1;
    raycaster.setFromCamera(pointer, world.camera);
    return raycaster.ray.intersectPlane(dragPlane, out) !== null;
  }

  function startDrag(p: PressState): void {
    const body = physics.bodyForGroup(p.group);
    if (!body) return;

    body.wakeUp();
    // The joint solver can yank the cup into the shelf or floor at speeds
    // past the 8 m/s fracture threshold. Suppress fracture for the held cup
    // while it's dragged; it'll re-arm on release so a thrown cup still
    // breaks on impact.
    physics.setFractureProtected(p.group, true);

    // Plane facing the camera, through the original hit point. Pointer
    // motion is projected onto this plane to give a 3D world-space target.
    const camForward = new THREE.Vector3();
    world.camera.getWorldDirection(camForward);
    dragPlane.setFromNormalAndCoplanarPoint(camForward.negate(), p.hit);

    // Invisible kinematic helper that follows the cursor. No collider on
    // it — only the joint binds it to the cup. Each pointermove updates
    // its target via setDragHelperPosition.
    const helperBody = physics.createDragHelper({
      x: p.hit.x,
      y: p.hit.y,
      z: p.hit.z,
    });

    // Pivot on the cup body in cup-local space: world offset from body
    // center to hit point, rotated by the body's inverse rotation.
    const tr = body.translation();
    const rot = body.rotation();
    const grabOffsetLocal = new THREE.Vector3(
      p.hit.x - tr.x,
      p.hit.y - tr.y,
      p.hit.z - tr.z,
    );
    const invRot = new THREE.Quaternion(rot.x, rot.y, rot.z, rot.w).invert();
    grabOffsetLocal.applyQuaternion(invRot);

    // Spherical joint locks linear position at the anchors, leaves
    // rotation free; the engine resolves it against collisions naturally.
    const joint = physics.attachDragJoint(body, helperBody, {
      x: grabOffsetLocal.x,
      y: grabOffsetLocal.y,
      z: grabOffsetLocal.z,
    });

    p.drag = {
      body,
      helperBody,
      joint,
      samples: [
        {
          pos: new THREE.Vector3(p.hit.x, p.hit.y, p.hit.z),
          t: performance.now(),
        },
      ],
      grabOffsetLocal,
    };

    // Drop hover lift while held.
    if (hovered) hovered.scale.set(1, 1, 1);
    canvas.style.cursor = "grabbing";
  }

  function endDrag(applyThrow: boolean): void {
    const drag = press?.drag;
    if (!drag) return;

    physics.releaseDragJoint(drag.joint);
    physics.removeBody(drag.helperBody);

    if (applyThrow && drag.samples.length >= 2) {
      const now = performance.now();
      const cutoff = now - VELOCITY_WINDOW_MS;
      let oldest = drag.samples[0];
      for (const s of drag.samples) {
        if (s.t >= cutoff) {
          oldest = s;
          break;
        }
      }
      const recent = drag.samples[drag.samples.length - 1];
      const dt = Math.max(0.016, (recent.t - oldest.t) / 1000);
      const vx = ((recent.pos.x - oldest.pos.x) / dt) * THROW_FEEL_MULT;
      const vy = ((recent.pos.y - oldest.pos.y) / dt) * THROW_FEEL_MULT;
      const vz = ((recent.pos.z - oldest.pos.z) / dt) * THROW_FEEL_MULT;
      drag.body.setLinvel({ x: vx, y: vy, z: vz }, true);

      const speed = Math.hypot(vx, vy, vz);
      const spin = Math.min(speed * 0.6, 7);
      drag.body.setAngvel(
        {
          x: (Math.random() - 0.5) * spin,
          y: (Math.random() - 0.5) * spin,
          z: (Math.random() - 0.5) * spin,
        },
        true,
      );
    }

    drag.body.wakeUp();
    // Re-arm fracture before clearing the drag. A thrown cup hitting
    // the floor at speed should still shatter.
    const draggedGroup = physics.groupForBody(drag.body);
    if (draggedGroup) physics.setFractureProtected(draggedGroup, false);
    world.controls.enabled = true;
    canvas.style.cursor = hovered ? "grab" : "default";
    // Caller is responsible for clearing `press` if it wants to fully
    // reset; the typical pointerup path does this immediately after.
    if (press) press.drag = null;
  }

  canvas.addEventListener("pointermove", (e) => {
    if (!enabled) return;
    const drag = press?.drag;
    if (drag) {
      const target = new THREE.Vector3();
      if (pointerToPlane(e.clientX, e.clientY, target)) {
        // Joint constraint: body_pos + body_rot * grabOffsetLocal == helper_pos.
        // For body_pos.y >= CUP_HALF_HEIGHT (cup not tunneling through floor),
        // require helper.y >= CUP_HALF_HEIGHT + (body_rot * grabOffsetLocal).y.
        // Without this clamp, the joint can outmuscle the floor's contact
        // constraint when the user drags the cursor below the floor.
        const rot = drag.body.rotation();
        tmpRotQuat.set(rot.x, rot.y, rot.z, rot.w);
        tmpOffsetWorld.copy(drag.grabOffsetLocal).applyQuaternion(tmpRotQuat);
        const minY = CUP_HALF_HEIGHT + tmpOffsetWorld.y;
        if (target.y < minY) target.y = minY;
        physics.setDragHelperPosition(drag.helperBody, {
          x: target.x,
          y: target.y,
          z: target.z,
        });
        drag.samples.push({ pos: target.clone(), t: performance.now() });
        if (drag.samples.length > MAX_SAMPLES) drag.samples.shift();
      }
      return;
    }

    const hit = pickAt(e.clientX, e.clientY);
    setHover(hit?.group ?? null);

    if (press) {
      const dx = e.clientX - press.downX;
      const dy = e.clientY - press.downY;
      if (dx * dx + dy * dy > DRAG_THRESHOLD_PX * DRAG_THRESHOLD_PX) {
        startDrag(press);
      }
    }
  });

  canvas.addEventListener("pointerleave", () => {
    setHover(null);
  });

  canvas.addEventListener("pointerdown", (e) => {
    if (!enabled) return;
    if (e.button !== 0) return;
    const hit = pickAt(e.clientX, e.clientY);
    if (hit) {
      press = {
        group: hit.group,
        hit: hit.point,
        downX: e.clientX,
        downY: e.clientY,
        drag: null,
      };
      world.controls.enabled = false;
    } else {
      press = null;
    }
  });

  canvas.addEventListener("pointerup", (e) => {
    if (!enabled) return;
    if (press?.drag) {
      endDrag(true);
    } else if (press) {
      const dx = e.clientX - press.downX;
      const dy = e.clientY - press.downY;
      if (dx * dx + dy * dy <= DRAG_THRESHOLD_PX * DRAG_THRESHOLD_PX) {
        const cup = cupsById.get(press.group.userData.cupId)?.cup ?? null;
        for (const cb of subscribers) cb(cup);
      }
      world.controls.enabled = true;
    }
    press = null;
  });

  canvas.addEventListener("pointercancel", () => {
    if (!enabled) return;
    if (press?.drag) {
      endDrag(false);
    } else if (press) {
      world.controls.enabled = true;
    }
    press = null;
  });

  return {
    onSelect: (cb) => subscribers.push(cb),
    setCups: (m) => {
      cupsById = m;
    },
    setEnabled: (v) => {
      enabled = v;
      if (!v) {
        if (press?.drag) endDrag(false);
        press = null;
        setHover(null);
        world.controls.enabled = true;
      }
    },
  };
}

function findCupId(obj: THREE.Object3D): string | null {
  let cur: THREE.Object3D | null = obj;
  while (cur) {
    if (cur.userData?.cupId) return cur.userData.cupId as string;
    cur = cur.parent;
  }
  return null;
}
